import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/features/auth';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';

import { useCustomerLimits } from '../api/purchase-limits.queries';
import { describeLimit, describeUsage } from '../lib/purchase-limit-labels';
import type { PurchaseLimit } from '../types/purchase-limit.types';
import { LimitStatusDialog } from './limit-status-dialog';
import { PurchaseLimitDialog } from './purchase-limit-dialog';

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; limit: PurchaseLimit }
  | { kind: 'status'; limit: PurchaseLimit }
  | null;

/**
 * The limits that apply to this customer now — their own, or their country's
 * where they have none of that kind — with what they used and what is left.
 */
export function CustomerLimitsSection({
  customerId,
  customerName,
}: {
  customerId: number;
  customerName: string;
}) {
  const states = useCustomerLimits(customerId);
  const { can } = usePermissions();
  const canManage = can('customers.limits.manage');
  const [open, setOpen] = useState<Open>(null);
  const close = () => setOpen(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Purchase limits</CardTitle>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ kind: 'create' })}>
            <Plus className="size-3.5" aria-hidden />
            Limit for this customer
          </Button>
        )}
      </CardHeader>
      <CardContent className="text-xs">
        {states.isLoading ? (
          <Skeleton className="h-16" />
        ) : states.error ? (
          <ErrorState error={states.error} onRetry={() => void states.refetch()} />
        ) : (states.data ?? []).length === 0 ? (
          <p className="text-muted-foreground">
            No limit applies — this customer can buy without a limit.{' '}
            <Link to="/customers/limits" className="text-primary hover:underline">
              All purchase limits
            </Link>
          </p>
        ) : (
          <ul aria-label="Limits that apply" className="divide-y">
            {(states.data ?? []).map((state) => {
              const over = state.remaining <= 0;
              return (
                <li key={state.limit.id} className="flex items-start justify-between gap-3 py-2">
                  <div>
                    <p className="font-medium">{describeLimit(state.limit)}</p>
                    <p className="text-muted-foreground">
                      {state.isCustomerSpecific
                        ? 'This customer’s own limit'
                        : 'Country limit for every customer'}
                      {' · '}used {describeUsage(state.limit, state.used)}, left{' '}
                      {describeUsage(state.limit, state.remaining)}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1">
                    {over && <StatusBadge variant="warning">Limit reached</StatusBadge>}
                    {canManage && state.isCustomerSpecific && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setOpen({ kind: 'edit', limit: state.limit })}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setOpen({ kind: 'status', limit: state.limit })}
                        >
                          Switch off
                        </Button>
                      </>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {open?.kind === 'create' && (
        <PurchaseLimitDialog
          target={{ kind: 'customer', customerId, customerName }}
          onClose={close}
        />
      )}
      {open?.kind === 'edit' && (
        <PurchaseLimitDialog
          target={{ kind: 'customer', customerId, customerName }}
          limit={open.limit}
          onClose={close}
        />
      )}
      {open?.kind === 'status' && <LimitStatusDialog limit={open.limit} onClose={close} />}
    </Card>
  );
}
